/**
 * Ajustes de la cuenta de un socio: cargar o perdonar plata que no es consumo.
 *
 * Hasta el recorrido P3 el saldo era exactamente `Σ boletas − Σ pagos`, así
 * que no había con qué cobrar una cuota de incorporación ni condonar una
 * deuda. Acá se ven y se cargan esos asientos.
 *
 * Un **cargo** se cobra en la boleta siguiente, por la línea OTROS (exenta de
 * IVA); un **abono** descuenta del saldo de inmediato.
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus } from "lucide-react";

import { ApiError, formatCLP, formatDate } from "../../lib/api";
import { useToast } from "../../components/Toast";
import { getAuth, hasPermission } from "../../lib/auth";
import { Modal } from "../../components/Modal";
import {
  Badge,
  Button,
  Cargando,
  ErrorCarga,
  Input,
  Select,
  TableWrap,
  Textarea,
  useDescarteProtegido,
  useHayCambios,
} from "../../components/ui";
import {
  anularAjuste,
  crearAjuste,
  listarAjustes,
  type AjusteRead,
  type ConceptoAjuste,
  type TipoAjuste,
} from "./api";

const CONCEPTOS: { value: ConceptoAjuste; label: string }[] = [
  { value: "CuotaIncorporacion", label: "Cuota de incorporación" },
  { value: "DerechoArranque", label: "Derecho de arranque" },
  { value: "TraspasoDeuda", label: "Deuda traspasada" },
  { value: "Condonacion", label: "Condonación" },
  { value: "Otro", label: "Otros" },
];

/** Invalida todo lo que cambia de valor cuando se mueve un ajuste. */
function useRefrescarCuenta(socioId: number): () => void {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ["ajustes-socio", socioId] });
    void qc.invalidateQueries({ queryKey: ["resumen-cuenta", socioId] });
    void qc.invalidateQueries({ queryKey: ["lecturas-pendientes", socioId] });
  };
}

export function AjustesSocioSection({
  socioId,
  contratoId,
}: {
  socioId: number;
  contratoId?: number | undefined;
}): JSX.Element {
  const [creando, setCreando] = useState(false);
  const puedeGestionar = hasPermission(getAuth(), "ajustes.gestionar");

  const ajustesQ = useQuery({
    queryKey: ["ajustes-socio", socioId],
    queryFn: () => listarAjustes(socioId, true),
    enabled: Number.isFinite(socioId),
  });

  const ajustes = ajustesQ.data ?? [];

  return (
    <div className="space-y-3">
      {puedeGestionar && (
        <div className="flex justify-end">
          <Button size="sm" icon={Plus} onClick={() => setCreando(true)}>
            Nuevo ajuste
          </Button>
        </div>
      )}

      {ajustesQ.isPending ? (
        <Cargando inline />
      ) : ajustesQ.isError ? (
        <ErrorCarga
          error={ajustesQ.error}
          mensaje="No se pudieron cargar."
          onReintentar={() => void ajustesQ.refetch()}
        />
      ) : ajustes.length === 0 ? (
        <p className="text-sm text-neutral-500">
          Sin ajustes. Acá aparecen la cuota de incorporación, el derecho de
          arranque, la deuda traspasada y las condonaciones.
        </p>
      ) : (
        <TableWrap label="Ajustes de cuenta">
          <table className="min-w-full divide-y divide-neutral-200 text-sm">
            <thead className="bg-neutral-50 text-xs uppercase tracking-wider text-neutral-500">
              <tr>
                <th scope="col" className="px-3 py-2 text-left">Fecha</th>
                <th scope="col" className="px-3 py-2 text-left">Concepto</th>
                <th scope="col" className="px-3 py-2 text-left">Motivo</th>
                <th scope="col" className="px-3 py-2 text-right">Monto</th>
                <th scope="col" className="px-3 py-2 text-left">Estado</th>
                <th scope="col" className="px-3 py-2 text-right">{""}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {ajustes.map((a) => (
                <FilaAjuste
                  key={a.id}
                  ajuste={a}
                  socioId={socioId}
                  puedeGestionar={puedeGestionar}
                />
              ))}
            </tbody>
          </table>
        </TableWrap>
      )}

      {creando && (
        <NuevoAjusteModal
          socioId={socioId}
          contratoId={contratoId}
          onClose={() => setCreando(false)}
        />
      )}
    </div>
  );
}

function FilaAjuste({
  ajuste: a,
  socioId,
  puedeGestionar,
}: {
  ajuste: AjusteRead;
  socioId: number;
  puedeGestionar: boolean;
}): JSX.Element {
  const toast = useToast();
  const refrescar = useRefrescarCuenta(socioId);
  const [anulando, setAnulando] = useState(false);
  const [motivo, setMotivo] = useState("");

  const mut = useMutation({
    mutationFn: () => anularAjuste(a.id, motivo.trim()),
    onSuccess: () => {
      toast.success("Ajuste anulado");
      refrescar();
      setAnulando(false);
    },
    onError: (err) =>
      toast.error(err instanceof ApiError ? err.message : "No se pudo anular"),
  });

  const anulado = a.estado === "Anulado";
  const esCargo = a.tipo === "Cargo";

  return (
    <tr className={anulado ? "text-neutral-500" : undefined}>
      <td className="px-3 py-2 text-neutral-600">{formatDate(a.fecha)}</td>
      <td className="px-3 py-2">{a.etiqueta}</td>
      <td className="px-3 py-2 text-neutral-700">
        {a.motivo}
        {a.observacion && (
          <span className="block text-xs text-neutral-500">{a.observacion}</span>
        )}
      </td>
      <td
        className={`px-3 py-2 text-right font-medium ${
          anulado ? "" : esCargo ? "text-neutral-900" : "text-emerald-700"
        }`}
      >
        {esCargo ? "" : "−"}
        {formatCLP(a.monto)}
      </td>
      <td className="px-3 py-2">
        {anulado ? (
          <span title={a.motivo_anulacion ?? undefined}>
            <Badge tone="rose">Anulado</Badge>
          </span>
        ) : a.lectura_id !== null ? (
          <Badge tone="brand">Cobrado en boleta</Badge>
        ) : esCargo ? (
          <Badge tone="amber">Por cobrar</Badge>
        ) : (
          <Badge tone="emerald">Aplicado</Badge>
        )}
      </td>
      <td className="px-3 py-2 text-right">
        {puedeGestionar && !anulado && (
          <Button variant="linkDanger" size="sm" onClick={() => setAnulando(true)}>
            Anular
          </Button>
        )}
        {anulando && (
          <Modal
            open
            onClose={() => setAnulando(false)}
            title="Anular ajuste"
            subtitle={`${a.etiqueta} · ${formatCLP(a.monto)}`}
            footer={
              <>
                <Button
                  variant="secondary"
                  onClick={() => setAnulando(false)}
                  disabled={mut.isPending}
                >
                  Cancelar
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => mut.mutate()}
                  disabled={mut.isPending || motivo.trim().length < 3}
                >
                  {mut.isPending ? "Anulando…" : "Anular"}
                </Button>
              </>
            }
          >
            <div className="space-y-3 text-left">
              {a.lectura_id !== null && (
                <p className="rounded-md bg-amber-50 p-2 text-xs text-amber-800">
                  Este cargo ya se cobró en una boleta. Anularlo lo invalida a
                  futuro, pero la boleta emitida lo conserva.
                </p>
              )}
              <Input
                label="Motivo"
                required
                value={motivo}
                onChange={(e) => setMotivo(e.target.value)}
                disabled={mut.isPending}
              />
            </div>
          </Modal>
        )}
      </td>
    </tr>
  );
}

function NuevoAjusteModal({
  socioId,
  contratoId,
  onClose,
}: {
  socioId: number;
  contratoId?: number | undefined;
  onClose: () => void;
}): JSX.Element {
  const toast = useToast();
  const refrescar = useRefrescarCuenta(socioId);

  const [tipo, setTipo] = useState<TipoAjuste>("Cargo");
  const [concepto, setConcepto] = useState<ConceptoAjuste>("CuotaIncorporacion");
  const [monto, setMonto] = useState("");
  const [motivo, setMotivo] = useState("");
  const [fecha, setFecha] = useState(new Date().toISOString().slice(0, 10));
  const [observacion, setObservacion] = useState("");
  const [error, setError] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: () =>
      crearAjuste({
        socio_id: socioId,
        tipo,
        concepto,
        monto: Number(monto),
        motivo: motivo.trim(),
        contrato_id: contratoId ?? null,
        observacion: observacion.trim() || null,
        fecha: fecha || null,
      }),
    onSuccess: () => {
      toast.success(
        tipo === "Cargo"
          ? "Cargo agregado · se cobra en la boleta siguiente"
          : "Abono aplicado al saldo",
      );
      refrescar();
      onClose();
    },
    onError: (err) => {
      const msg = err instanceof ApiError ? err.message : "No se pudo guardar";
      setError(msg);
      toast.error(msg);
    },
  });

  // Cambios sin guardar: cerrar pregunta antes de descartar (auditoría UX-01).
  const hayCambios = useHayCambios([tipo, concepto, monto, motivo, fecha, observacion]);
  const { cerrar: intentarCerrar, dialogo: dialogoDescarte } = useDescarteProtegido(
    hayCambios,
    onClose,
    mut.isPending,
  );

  return (
    <Modal
      open
      onClose={intentarCerrar}
      title="Nuevo ajuste de cuenta"
      size="lg"
      closeOnBackdrop={false}
      footer={
        <>
          <Button variant="secondary" onClick={intentarCerrar} disabled={mut.isPending}>
            Cancelar
          </Button>
          <Button
            onClick={() => {
              setError(null);
              const n = Number(monto);
              if (!Number.isFinite(n) || n <= 0) {
                setError("El monto tiene que ser mayor que 0");
                return;
              }
              if (motivo.trim() === "") {
                setError("Escribe el motivo: es plata que mueves tú");
                return;
              }
              mut.mutate();
            }}
            disabled={mut.isPending}
          >
            {mut.isPending ? "Guardando…" : "Guardar"}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <Select
            label="Tipo"
            value={tipo}
            onChange={(e) => setTipo(e.target.value as TipoAjuste)}
            disabled={mut.isPending}
          >
            <option value="Cargo">Cargo (el socio pasa a deber)</option>
            <option value="Abono">Abono (se le descuenta del saldo)</option>
          </Select>
          <Select
            label="Concepto"
            value={concepto}
            onChange={(e) => setConcepto(e.target.value as ConceptoAjuste)}
            disabled={mut.isPending}
          >
            {CONCEPTOS.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </Select>
          <Input
            label="Monto (CLP)"
            type="number"
            min={1}
            step={1}
            required
            value={monto}
            onChange={(e) => setMonto(e.target.value)}
            disabled={mut.isPending}
          />
          <Input
            label="Fecha"
            type="date"
            value={fecha}
            onChange={(e) => setFecha(e.target.value)}
            disabled={mut.isPending}
          />
        </div>
        <Input
          label="Motivo"
          required
          hint="Queda en la bitácora junto a tu nombre"
          value={motivo}
          onChange={(e) => setMotivo(e.target.value)}
          disabled={mut.isPending}
        />
        <Textarea
          label="Observación (opcional)"
          rows={2}
          value={observacion}
          onChange={(e) => setObservacion(e.target.value)}
          disabled={mut.isPending}
        />

        <p className="text-xs text-neutral-500">
          {tipo === "Cargo"
            ? "El cargo se cobra en la boleta siguiente, en la línea OTROS. Va exento de IVA: no es venta de servicio sanitario."
            : "El abono se descuenta del saldo de inmediato. No viaja en ninguna boleta."}
        </p>

        {error && (
          <p role="alert" className="text-sm text-rose-700">
            {error}
          </p>
        )}
      </div>
      {dialogoDescarte}
    </Modal>
  );
}
